<template>
  <div class="or-page">
    <section class="or-hero">
      <div>
        <h2 class="or-hero__title">
          Ongoing Requests
        </h2>
        <p class="or-hero__subtitle">
          Every client with an open marketer-support request — help the CE get
          what they need from the client, any time. Closed requests (issued or
          rejected) drop off this list automatically.
        </p>
      </div>
      <div class="or-hero__stats">
        <div class="or-stat">
          <span class="or-stat__value">{{ clients.length }}</span>
          <span class="or-stat__label">Clients</span>
        </div>
        <div class="or-stat">
          <span class="or-stat__value">{{ requestCount }}</span>
          <span class="or-stat__label">Open requests</span>
        </div>
        <div class="or-stat or-stat--client">
          <span class="or-stat__value">{{ atClientCount }}</span>
          <span class="or-stat__label">At client</span>
        </div>
        <div class="or-stat or-stat--ministry">
          <span class="or-stat__value">{{ atMinistryCount }}</span>
          <span class="or-stat__label">At ministry</span>
        </div>
      </div>
    </section>

    <section class="or-filters">
      <input
        v-model="search"
        type="text"
        class="or-input"
        placeholder="Search client or company…"
      >
      <div class="or-bucket-toggle">
        <button
          type="button"
          class="or-bucket-btn"
          :class="{ 'or-bucket-btn--active': bucket === '' }"
          @click="bucket = ''"
        >
          All
        </button>
        <button
          type="button"
          class="or-bucket-btn or-bucket-btn--client"
          :class="{ 'or-bucket-btn--active': bucket === 'at_client' }"
          @click="bucket = 'at_client'"
        >
          At client
        </button>
        <button
          type="button"
          class="or-bucket-btn or-bucket-btn--ministry"
          :class="{ 'or-bucket-btn--active': bucket === 'at_ministry' }"
          @click="bucket = 'at_ministry'"
        >
          At ministry
        </button>
      </div>
    </section>

    <!--
      Karim, 22 Sep: "i cannot filter by product and certificate and i wish i
      can filter like we can filter in dashboard analysis outside crm." Same
      two fields, same multi-select-with-ctrl/cmd UX, same source
      (ApplicationsAnalyticsQuery) as the Applications Analytics dashboard —
      not a second, different idea of what "product"/"certificate" means.
    -->
    <section class="or-filters or-filters--advanced">
      <div class="or-select-field">
        <label>
          Certificate Scheme
          <span class="or-select-field__hint">({{ filterOptions.certificateSchemes.length }}, type to search)</span>
        </label>
        <v-select
          v-model="selectedSchemes"
          :options="filterOptions.certificateSchemes"
          multiple
          placeholder="Search…"
          @input="load"
        />
      </div>
      <div class="or-select-field">
        <label>
          Product Category
          <span class="or-select-field__hint">({{ filterOptions.productCategories.length }}, type to search)</span>
        </label>
        <v-select
          v-model="selectedCategories"
          :options="filterOptions.productCategories"
          multiple
          placeholder="Search…"
          @input="load"
        />
      </div>
      <button
        type="button"
        class="or-refresh"
        :disabled="loading"
        @click="load"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="14"
        />
        Apply filters
      </button>
    </section>

    <div
      v-if="loading"
      class="or-loading"
    >
      Loading…
    </div>

    <div
      v-else-if="loadError"
      class="alert alert-info m-3"
    >
      {{ loadError }}
    </div>

    <p
      v-else-if="!filteredClients.length"
      class="or-empty"
    >
      No open marketer-support requests right now.
    </p>

    <div
      v-else
      class="or-list"
    >
      <article
        v-for="row in filteredClients"
        :key="row.clientId || row.requests[0].cardId"
        class="or-client-card"
      >
        <header class="or-client-card__head">
          <div>
            <h3 class="or-client-card__name">
              {{ row.clientName || 'Unnamed contact' }}
            </h3>
            <p class="or-client-card__company">
              {{ row.companyName || '—' }}
              <span v-if="row.country">· {{ row.country }}</span>
            </p>
          </div>
          <router-link
            v-if="row.clientId"
            class="or-btn or-btn--ghost"
            :to="{ name: 'crm-system-contact-show', params: { id: row.clientId } }"
          >
            View client
          </router-link>
        </header>

        <ul class="or-request-list">
          <li
            v-for="req in row.requests"
            :key="req.cardId"
            class="or-request-row"
          >
            <span
              class="or-pill"
              :class="req.bucket === 'at_client' ? 'or-pill--client' : 'or-pill--ministry'"
            >
              {{ req.bucket === 'at_client' ? 'At client' : req.bucket === 'at_ministry' ? 'At ministry' : 'Unknown' }}
            </span>
            <span class="or-request-row__code">{{ req.requestCode || ('#' + req.requestId) }}</span>
            <span class="or-request-row__cert">{{ req.certificateName || req.source || '—' }}</span>
            <span
              v-if="req.certificateScheme || req.productCategory"
              class="or-request-row__cert or-request-row__cert--muted"
            >{{ [req.certificateScheme, req.productCategory].filter(Boolean).join(' · ') }}</span>
            <span class="or-request-row__status">{{ req.enternalStatus || req.status || '—' }}</span>
            <span class="or-request-row__marketer">
              <feather-icon
                icon="UserIcon"
                size="12"
              />
              {{ req.marketerName || 'Unassigned' }}
            </span>
          </li>
        </ul>
      </article>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import vSelect from 'vue-select'
// Karim, 23 Sep, screenshot: see the same fix on CompaniesCRM.vue — this
// component never imported vue-select's own CSS, so it only looked right
// when another page that does (e.g. Leads) had already loaded it first.
import 'vue-select/dist/vue-select.css'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'OngoingRequestsCRM',
  components: {
    vSelect,
  },
  data() {
    return {
      loading: false,
      loadError: null,
      clients: [],
      search: '',
      bucket: '',
      selectedSchemes: [],
      selectedCategories: [],
      filterOptions: {
        certificateSchemes: [],
        productCategories: [],
      },
    }
  },
  computed: {
    requestCount() {
      return this.clients.reduce((sum, c) => sum + c.requests.length, 0)
    },
    atClientCount() {
      return this.clients.reduce((sum, c) => sum + c.requests.filter(r => r.bucket === 'at_client').length, 0)
    },
    atMinistryCount() {
      return this.clients.reduce((sum, c) => sum + c.requests.filter(r => r.bucket === 'at_ministry').length, 0)
    },
    filteredClients() {
      const needle = this.search.trim().toLowerCase()

      return this.clients
        .map(row => ({
          ...row,
          requests: this.bucket ? row.requests.filter(r => r.bucket === this.bucket) : row.requests,
        }))
        .filter(row => row.requests.length)
        .filter(row => {
          if (!needle) return true

          return (row.clientName || '').toLowerCase().includes(needle)
            || (row.companyName || '').toLowerCase().includes(needle)
        })
    },
  },
  mounted() {
    this.loadFilterOptions()
    this.load()
  },
  methods: {
    async loadFilterOptions() {
      try {
        const res = await axios.get('crm/ongoing-requests/filters')
        const data = decryptData(res.data.payload).data || {}
        this.filterOptions = {
          certificateSchemes: data.certificateSchemes || [],
          productCategories: data.productCategories || [],
        }
      } catch (e) {
        // Filters just won't have options to pick from; the page itself still works.
      }
    },
    async load() {
      this.loading = true
      this.loadError = null
      try {
        const params = {}
        if (this.selectedSchemes.length) params.certificateScheme = this.selectedSchemes
        if (this.selectedCategories.length) params.productCategory = this.selectedCategories

        const res = await axios.get('crm/ongoing-requests', { params })
        const data = decryptData(res.data.payload).data || {}
        this.clients = Array.isArray(data.clients) ? data.clients : []
      } catch (e) {
        this.loadError = 'Could not load ongoing requests.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" src="./ongoing-requests-crm.scss" scoped></style>
